import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Autocomplete,
  TextField,
  Chip,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";
import TableComponent from "components/grid";
import AddDataAlert from "./AddDataAlert";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import PaginationComponent from "components/ui-components/PaginationComponent";
import { useParams } from "react-router-dom";
import moment from "moment";
import { IoMdClose, IoMdSearch } from "react-icons/io";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { useDeleteDataAlertMutation, useGetAllDataAlertsQuery } from "store/slices/apis/dataAlertManagementSlice";
import EditDataAlert from "./EditDataAlert";
import { IoIosSearch } from "react-icons/io";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import { usePermission } from "hooks/usePermission";
import { DATA_METRICS_VIEW, DATA_METRICS_DELETE, DATA_METRICS_EDIT, DATA_METRICS_CREATE } from "config";
import {ReactComponent as AddNewIcon} from "assets/PwC_Funct_Icons_Plus_Outline_Black_RGB.svg"
import {ReactComponent as SearchIcon} from "assets/PwC_Funct_Icons_Search_Outline_Black_RGB.svg"
import {ReactComponent as CrossIcon } from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg"

const approvedStatuses = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const AllDataAlertManagement = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const viewAccess = usePermission(DATA_METRICS_VIEW);
  const addAccess = usePermission(DATA_METRICS_CREATE);
  const editAccess = usePermission(DATA_METRICS_EDIT);
  const deleteAccess = usePermission(DATA_METRICS_DELETE);

  const [showAddIssue, setShowAddIssue] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [deleteItem, setDeleteItem] = useState(null);
  const [displayData, setDisplayData] = useState([]);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [isEditIssueModal, setEditIssueModal] = useState(false);
  const [issueData, setEditIssueData] = useState(null);
  const [isViewIssueModal, setIsViewIssueModal] = useState(false);
  const [approvedStatus, setApprovedStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    approvedStatus: "",
    fromDate: "",
    toDate: "",
  });
  const pageSize = 10;
  const { projectId } = useParams();
  const {
    data,
    refetch,
    isLoading,
  } = useGetAllDataAlertsQuery({
    ProjectId: projectId,
    sortBy: "CreatedDate",
    sortOrder: "desc",
    // from: filters?.fromDate,
    // to: filters?.toDate,
    page: pageNumber,
    pageSize: pageSize,
    searchTerm: searchQuery,
    status: "",
    // status: "ACTIVE",
    status: filters?.approvedStatus,
  });
  const [alertDeleteFn, alertDeleteRes] = useDeleteDataAlertMutation();

  useEffect(() => {
    if (isLoading) {
      setDisplayData([]);
    } else if (data?.data) {
      const updated = data.data.map((item, idx) => ({
        ...item,
        sr_no: (pageNumber - 1) * pageSize + idx + 1,
      }));
      setDisplayData(updated);
    }
  }, [isLoading, data, pageNumber]);

  const columns = [
    {
      name: "S. No.",
      field: "sr_no",
    }
    ,
    {
      name: "Alert Name",
      field: "alertName",
    },
    {
      name: "Interval",
      field: "interval",
    },
    {
      name: "Start Date",
      field: "startDate",
      renderer(params) {
        return (
          <>
            {params?.startDate
              ? moment(params.startDate).format("DD/MM/YY")
              : "N.A."}
          </>
        );
      },
    },
    {
      name: "End Date",
      field: "endDate",
      renderer(params) {
        return (
          <>
            {params?.endDate
              ? moment(params.endDate).format("DD/MM/YY")
              : "N.A."}
          </>
        );
      },
    },
    {
      name: "Created at",
      field: "createdDate",
      renderer(params) {
        return (
          <>
            {params?.createdDate
              ? moment(params?.createdDate).format("DD/MM/YY")
              : "N.A."}
          </>
        );
      },
    },
    {
      name: "Status",
      field: "status",
    },
    {
      name: "Action",
      field: "action",
      option: {},
    },
  ];

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleAddIssue = () => {
    setShowAddIssue(false); // ✅ Explicitly close it
  };

  const openNotification = async (msg, type = "error") => {
    await dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: {
          color: type,
        },
        close: false,
      })
    );

    setTimeout(() => dispatch(closeSnackbar()), 3000);
  };

  const handleActionEdit = (row) => {
    setEditIssueModal(!isEditIssueModal);
    if (!issueData) {
      setEditIssueData(row);
    } else {
      setEditIssueData(null);
    }
  };

  const handleActionView = (row) => {
    setIsViewIssueModal(!isViewIssueModal);
    if (!issueData) {
      setEditIssueData(row);
    } else {
      setEditIssueData(null);
    }
  };
  const handleActionDelete = (row) => {
    setDeleteItem(row);
    setDeleteModal(true);
  };

  const handleConfirmationDelete = () => {
    alertDeleteFn(deleteItem?.id);
  };

  useEffect(() => {
    if (alertDeleteRes?.isSuccess) {
      if (alertDeleteRes?.data?.status == "success") {
        openNotification(alertDeleteRes?.data?.message, "success");
        refetch();
      } else {
        openNotification(alertDeleteRes?.data?.message, "error");
      }
      handleCloseDelete();
      refetch();
    }
  }, [alertDeleteRes?.isSuccess]);

  const handleCloseDelete = () => {
    setDeleteItem(null);
    setDeleteModal(false);
  };

  const confirmationDeleteDialog = () => (
    <Dialog open={isDeleteModal} aria-labelledby="draggable-dialog-title">
      <DialogTitle
        sx={{
          fontWeight: 500,
          fontSize: "20px",
          color: "#252525",
          paddingBottom: 0,
        }}
        id="draggable-dialog-title"
      >
        Delete
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleCloseDelete}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        {/* <IoMdClose style={{ fontSize: "20px" }} /> */}
        <CrossIcon style={{ fontSize: "20px", width:"20px", height:"20px", fill:"#808080 " }} />
      </IconButton>
      <DialogContent>
        <DialogContentText
          sx={{ fontWeight: 400, fontSize: "16px", color: "#474747" }}
        >
          Are you sure you want to Delete this data alert? This action is permanent.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleCloseDelete}
          sx={{
            color: "#415385",
            fontSize: "16px",
            fontWeight: "500",
            textTransform: "none",
            border: "1px solid #415385",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmationDelete}
          //loading={issueDeleteRes?.isLoading}
          variant="contained"
          sx={{
            backgroundColor: "#C52A1A",
            color: "#fff",
            fontSize: "16px",
            fontWeight: "500",
            textTransform: "none",
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {confirmationDeleteDialog()}
      <Grid2 container spacing={2} sx={{ mt: "20px" }}>
        <Grid2 size={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: 'start', sm: 'center' },
              justifyContent: "space-between",
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                // flexGrow: 2,
                alignItems: { xs: "stretch", sm: "center" }, // Full width on mobile
                width: { xs: '100%', sm: 'auto' }, // Stack takes full width
                "& .MuiStack-root": {
                  paddingTop: "15px",
                },
              }}
            >

              <TextField
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by Alert Name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {/* <IoIosSearch /> */}
                      <SearchIcon style={{ height: "20px", width: "20px", fill:"#808080" }} />
                    </InputAdornment>
                  ),
                  style: {
                    fontSize: "16px",
                  },
                  endAdornment: searchQuery?.length > 0 && (
                    <IconButton onClick={clearSearch} edge="end">
                       {/* <ClearIcon style={{ fontSize: "16px" }} /> */}
                    <CrossIcon style={{ fontSize: "20px", width:"20px", height:"20px", fill:"#808080" }} />
                    </IconButton>
                  ),
                }}
                sx={{
                  minWidth: "300px",
                  height: "48px",
                  maxWidth: "500px",
                  background: "#fff",
                  marginTop: "12px !important",
                  "& .MuiInputBase-root": {
                    height: "48px",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "16px",
                    lineHeight: "20px",
                    fontWeight: "400",
                    color: "#696969",
                    opacity: 1,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: "16px",
                    lineHeight: "20px",
                    fontWeight: "400",
                    color: "#696969",
                    opacity: 1,
                  },
                }}
              />
              <Autocomplete
                options={approvedStatuses}
                getOptionLabel={(option) => option?.label}
                onChange={(_, newValue) =>
                  setApprovedStatus(newValue ? newValue?.value : "")
                }
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    paddingTop: "8px !important",
                    paddingBottom: "7px !important",
                    paddingRight: "0px !important",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status"
                    name="approvedStatus"
                    // slotProps={{ inputLabel: { shrink: true } }}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "#fff",
                      },

                      // "& .MuiInputLabel-root": {
                      //   top: "-6px",
                      // },
                      minWidth: "230px",
                      marginTop: "12px",
                    }}
                  />
                )}
                ListboxProps={{
                  sx: { fontSize: "13px" },
                }}
              // sx={{ width: "23%", marginTop: "7px !important" }}
              />
              <Stack spacing={2} direction="row">
                <Button
                  //loading={isLoading}
                  fullWidth
                  onClick={() => {
                    setFilters({
                      approvedStatus,
                    });
                  }}
                  size="large"
                  startIcon={<SearchIcon style={{ height: "20px", width: "20px", fill:`${theme?.customSettings?.themeColor}` }} />}
                  variant="outlined"
                  sx={{
                    color: `${theme?.customSettings?.themeColor}`,
                    border: `1px solid ${theme?.customSettings?.themeColor}`,
                    background: "none",
                    textTransform: "inherit",
                    padding: "6px 20px",
                    // marginTop: "12px !important",
                  }}
                >
                  Search
                </Button>
                <Button
                  loading={isLoading}
                  fullWidth
                  onClick={() => {
                    setApprovedStatus(null)
                    const clearedValues = Object.fromEntries(
                      Object.keys(filters).map((key) => [key, ""])
                    );
                    setFilters(clearedValues);
                    clearSearch();
                  }}
                  size="large"
                  variant="outlined"
                  sx={{
                    color: `${theme?.customSettings?.themeColor}`,
                    border: `1px solid ${theme?.customSettings?.themeColor}`,
                    background: "none",
                    textTransform: "inherit",
                    padding: "6px 15px",
                    // marginTop: "12px !important",
                  }}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>
            {addAccess &&
              <Button
                variant="contained"
                onClick={() => setShowAddIssue(true)}
                //  startIcon={<AddIcon sx={{ marginBottom: "2px" }} />}
                startIcon={<AddNewIcon style={{ height: "20px", width: "20px", fill: "#FFFFFF" }}  />}
                sx={{
                  backgroundColor: `${theme?.customSettings?.themeColor}`,
                  color: "#FFF",
                  padding: "5px 20px",
                  fontWeight: "500",
                  textTransform: "none",
                  fontSize: "16px",
                  marginTop: "11px",
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                }}
              >
                Create New Alert
              </Button>
            }

          </Box>
        </Grid2>

        <Grid2 size={12} sx={{ mt: 2 }}>
          <TableComponent
            columns={columns
              .map((col) =>
                col.field === "action"
                  ? {
                    ...col,
                    option: {
                      ...col.option,
                      view: viewAccess,
                      delete: deleteAccess,
                      edit: editAccess
                    },
                  }
                  : col
              )
              .filter((col) => {
                if (col.field !== "action") {
                  return true;
                } else {
                  return Object.values(col.option).some((v) => v === true);
                }

              })}
            data={displayData}
            actionDelete={handleActionDelete}
            actionEdit={handleActionEdit}
            actionView={handleActionView}
            isLoading={isLoading}
          />
          {!isLoading && (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <PaginationComponent
                pageNumber={data?.pagination?.currentPage || 1}
                totalPages={data?.pagination?.totalPages || 1}
                onPageChange={setPageNumber}
              />
            </Box>
          )}
        </Grid2>
      </Grid2>
      {showAddIssue && (
        <AddDataAlert
          cancelIssue={handleAddIssue}
          open={showAddIssue}
          openNotification={openNotification}
        //refetch={refetch}
        />
      )}
      {isEditIssueModal && (
        <EditDataAlert
          cancelIssue={handleActionEdit}
          open={isEditIssueModal}
          data={issueData}
          openNotification={openNotification}
        // refetch={refetch}
        />
      )}

      {isViewIssueModal && (
        <EditDataAlert
          cancelIssue={handleActionView}
          open={isViewIssueModal}
          data={issueData}
          openNotification={openNotification}
          isViewIssueModal
        />
      )}
    </>
  );
};

export default AllDataAlertManagement;
